import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fulfillmentContextService, partnerManagementService } from '~/services';
import { ASSET_LIST_PAGE_SIZE } from '~/services/partner-management-service';
import { usePartnerCarrierSummaries } from '~/hooks/queries/usePartnerCarrierSummaries';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useSession } from '~/hooks/useSession';
import type { RuleTestShipFromAddress } from './buildRuleTestRequest';
import type { ActiveRuleSummary } from './types';

export interface DisabledRule {
  ruleId: string;
  name: string;
  description: string;
  position: number;
}

interface UseRuleTestDataParams {
  open: boolean;
  selectedFcId: string | undefined;
  optedInInactiveRuleIds: string[] | undefined;
}

export const useRuleTestData = ({ open, selectedFcId, optedInInactiveRuleIds }: UseRuleTestDataParams) => {
  const { sessionPartnerUrl, sessionPartnerId } = useSession();
  const { data: ruleSet } = useQuery({
    ...useQueryCreator(partnerManagementService.ruleSets.partnerRuleSet, { args: [] }),
    enabled: open && Boolean(sessionPartnerUrl),
  });
  const { data: rules } = useQuery({
    ...useQueryCreator(partnerManagementService.rules.search, { args: [] }),
    enabled: open && Boolean(sessionPartnerUrl),
  });
  const { data: fulfillmentContexts, isLoading: fcsLoading } = useQuery({
    ...useQueryCreator(partnerManagementService.fulfillmentContexts.search, {
      args: [{ count: ASSET_LIST_PAGE_SIZE }],
    }),
    enabled: open && Boolean(sessionPartnerUrl),
  });
  // Per-FC carrier detail; only fetched after the user picks an FC.
  const { data: fcDetail } = useQuery({
    ...useQueryCreator(
      {
        queryKey: 'rule-test/fcDetail',
        configurationType: 'default' as const,
        fn: (fulfillmentContextId: string) =>
          fulfillmentContextService.fetchFulfillmentContextDetail(fulfillmentContextId),
      },
      { args: [selectedFcId as string] },
    ),
    enabled: open && Boolean(selectedFcId),
  });
  const { data: partnerCarrierSummaries } = usePartnerCarrierSummaries({ partnerId: sessionPartnerId });

  const fcOptions = useMemo(
    () =>
      (fulfillmentContexts ?? [])
        .filter((fc) => fc.fulfillmentContextId)
        .map((fc) => ({ value: fc.fulfillmentContextId, label: fc.name ?? fc.fulfillmentContextId ?? '' })),
    [fulfillmentContexts],
  );

  // Disabled rules = entries in the ruleSet whose underlying rule has `active === false`.
  // Walk `ruleSet.rules` once in sequence order; the index becomes the 1-based position so
  // numbers shown here line up with the ruleset page.
  const disabledRules = useMemo<DisabledRule[]>(() => {
    const ruleById = new Map((rules ?? []).map((r) => [r.ruleId as string, r]));
    return [...(ruleSet?.rules ?? [])]
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .flatMap((entry, index): DisabledRule[] => {
        const r = entry.ruleId ? ruleById.get(entry.ruleId) : undefined;
        if (r?.active !== false) return [];
        return [
          {
            ruleId: r.ruleId as string,
            name: r.name ?? '(unnamed rule)',
            description: r.description ?? '',
            position: index + 1,
          },
        ];
      });
  }, [ruleSet, rules]);

  // serviceMethodId → display name and carrierId → display name lookups for the timeline and
  // carrier callout. The carrier summaries response carries both.
  const { methodNameById, carrierNameById } = useMemo(() => {
    if (!Array.isArray(partnerCarrierSummaries)) return { methodNameById: {}, carrierNameById: {} };
    const methods: Record<string, string> = {};
    const carriers: Record<string, string> = {};
    for (const carrier of partnerCarrierSummaries) {
      if (carrier.carrierId && carrier.name) carriers[carrier.carrierId] = carrier.name;
      for (const method of carrier.serviceMethods ?? []) {
        if (method.serviceMethodId && method.name) methods[method.serviceMethodId] = method.name;
      }
    }
    return { methodNameById: methods, carrierNameById: carriers };
  }, [partnerCarrierSummaries]);

  // Active rules in evaluation order. Opted-in inactives slot into their natural sequence
  // position so the timeline mirrors what the backend will evaluate.
  const activeRulesInOrder = useMemo<ActiveRuleSummary[]>(() => {
    const ruleById = new Map((rules ?? []).map((r) => [r.ruleId as string, r]));
    const optedInIds = new Set(optedInInactiveRuleIds ?? []);
    return [...(ruleSet?.rules ?? [])]
      .filter((entry) => {
        if (!entry.ruleId) return false;
        const r = ruleById.get(entry.ruleId);
        if (!r) return false;
        return r.active || optedInIds.has(entry.ruleId);
      })
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((entry, index) => {
        const r = ruleById.get(entry.ruleId as string);
        return {
          ruleId: entry.ruleId as string,
          name: r?.name ?? '(unnamed rule)',
          position: index + 1,
          listType: r?.listType,
          terminating: r?.terminating ?? false,
          targetMethodIds: r?.carrierAndServiceMethodList ?? [],
          inactive: r?.active === false,
        };
      });
  }, [ruleSet, rules, optedInInactiveRuleIds]);

  // carrierId → carrierServiceMethodId set tree for the FC's configured carriers.
  const carrierTreeData = useMemo(() => {
    if (!selectedFcId) return [];
    const fcCarrierMethods = new Map<string, Set<string>>();
    for (const contract of fcDetail?.contracts ?? []) {
      for (const rateWrapper of contract.carrierRates ?? []) {
        const rate = rateWrapper.carrierRate;
        if (!rate?.carrier || !rate.carrierServiceMethodId) continue;
        if (!fcCarrierMethods.has(rate.carrier)) fcCarrierMethods.set(rate.carrier, new Set());
        fcCarrierMethods.get(rate.carrier)!.add(rate.carrierServiceMethodId);
      }
    }
    if (fcCarrierMethods.size === 0) return [];
    const partnerByCarrierId = new Map(
      (Array.isArray(partnerCarrierSummaries) ? partnerCarrierSummaries : []).map((c) => [c.carrierId, c]),
    );
    return Array.from(fcCarrierMethods.entries())
      .map(([carrierId, methodIds]) => {
        const partnerCarrier = partnerByCarrierId.get(carrierId);
        const partnerMethods = partnerCarrier?.serviceMethods ?? [];
        const nameByMethodId = new Map(partnerMethods.map((m) => [m.serviceMethodId, m.name]));
        const children = Array.from(methodIds)
          .map((id) => ({ title: nameByMethodId.get(id) ?? id, value: id, key: id }))
          .sort((a, b) => a.title.localeCompare(b.title));
        return {
          title: partnerCarrier?.name ?? carrierId,
          value: carrierId,
          key: carrierId,
          selectable: false,
          children,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [selectedFcId, fcDetail, partnerCarrierSummaries]);

  const shipFromAddress = useMemo<RuleTestShipFromAddress | undefined>(() => {
    const loc = fcDetail?.origin?.address?.address?.location;
    if (!loc?.country || !loc?.postalCode) return undefined;
    return {
      countryCode: loc.country,
      postalCode: loc.postalCode,
      ...(loc.address1 ? { street1: loc.address1 } : {}),
      ...(loc.city ? { city: loc.city } : {}),
      ...(loc.region ? { state: loc.region } : {}),
    };
  }, [fcDetail]);

  return {
    fcOptions,
    fcsLoading,
    disabledRules,
    methodNameById,
    carrierNameById,
    activeRulesInOrder,
    carrierTreeData,
    shipFromAddress,
  };
};
